import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FileTextIcon, PlusIcon, SearchIcon, FilterIcon, DownloadIcon, PrinterIcon, CheckCircleIcon, ClockIcon, BookOpenIcon, EyeIcon, XIcon } from 'lucide-react';
import { useRental } from '../../contexts/RentalContext';
import { ContractDetailsDrawer } from '../../components/rental/ContractDetailsDrawer';
import { ContractPreviewModal } from '../../components/rental/ContractPreviewModal';
import type { Contract } from '../../contexts/RentalContext';
export function Contracts() {
  const navigate = useNavigate();
  const {
    contracts
  } = useRental();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewContract, setPreviewContract] = useState<Contract | null>(null);
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [clientTypeFilter, setClientTypeFilter] = useState<string>('all');
  const handleOpenPreview = (contract: Contract, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewContract(contract);
    setIsPreviewOpen(true);
  };
  const stats = [{
    label: 'Contracte Semnate',
    value: contracts.filter(c => c.status === 'signed').length,
    color: 'text-emerald-600',
    bg: 'bg-emerald-100'
  }, {
    label: 'În Așteptare',
    value: contracts.filter(c => c.status === 'sent').length,
    color: 'text-orange-600',
    bg: 'bg-orange-100'
  }, {
    label: 'Draft',
    value: contracts.filter(c => c.status === 'draft').length,
    color: 'text-slate-600',
    bg: 'bg-slate-100'
  }, {
    label: 'Total Contracte',
    value: contracts.length,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }];
  const filteredContracts = contracts.filter(contract => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = contract.clientName.toLowerCase().includes(query) || contract.id.toLowerCase().includes(query) || contract.propertyAddress.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    // Status filter
    if (statusFilter !== 'all' && contract.status !== statusFilter) {
      return false;
    }
    // Type filter
    if (typeFilter !== 'all' && contract.type !== typeFilter) {
      return false;
    }
    // Client type filter
    if (clientTypeFilter !== 'all' && contract.clientType !== clientTypeFilter) {
      return false;
    }
    return true;
  });
  const clearFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setClientTypeFilter('all');
    setSearchQuery('');
  };
  const activeFiltersCount = [statusFilter, typeFilter, clientTypeFilter].filter(f => f !== 'all').length;
  return <div className="min-h-screen pb-20" data-id="element-9182">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#132C53] to-[#1e4278] pb-16 pt-10" data-id="element-9183">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-9184" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-9185"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9186">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-9187">
            <div className="text-center md:text-left" data-id="element-9188">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 200
            }} className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 mb-6 shadow-2xl md:hidden" data-id="element-9189">
                <FileTextIcon className="w-8 h-8 text-white" data-id="element-9190" />
              </motion.div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-9191">
                Contracte &{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-9192">
                  Documente
                </span>
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl" data-id="element-9193">
                Generează și gestionează contractele de închiriere și
                intermediere
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} className="flex items-center space-x-3" data-id="element-9194">
              <button onClick={() => navigate('/inchiriere/contracte/tutorial')} className="flex items-center space-x-2 px-5 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all" data-id="element-9195">
                <BookOpenIcon className="w-5 h-5" data-id="element-9196" />
                <span data-id="element-9197">Tutorial</span>
              </button>
              <button onClick={() => navigate('/inchiriere/contracte/nou')} className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all" data-id="element-9198">
                <PlusIcon className="w-5 h-5" data-id="element-9199" />
                <span data-id="element-9200">Creează Contract</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10" data-id="element-9201">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-9202">
          {stats.map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-slate-100" data-id="element-9203">
              <p className="text-sm text-slate-500 font-medium mb-1" data-id="element-9204">
                {stat.label}
              </p>
              <div className="flex items-center space-x-2" data-id="element-9205">
                <span className={`text-2xl sm:text-3xl font-bold ${stat.color}`} data-id="element-9206">
                  {stat.value}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Contracts List */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden" data-id="element-9207">
          {/* Toolbar */}
          <div className="p-6 border-b border-slate-100 bg-slate-50/50" data-id="element-9208">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4" data-id="element-9209">
              <h2 className="text-xl font-bold text-[#132C53]" data-id="element-9210">
                Documente Recente
              </h2>

              <div className="flex items-center space-x-3 w-full sm:w-auto" data-id="element-9211">
                <div className="relative flex-1 sm:flex-none" data-id="element-9212">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" data-id="element-9213" />
                  <input type="text" placeholder="Caută contracte..." className="w-full sm:w-64 pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-9214" />
                </div>
                <button onClick={() => setShowFilters(!showFilters)} className={`relative p-2.5 rounded-xl border transition-all shadow-sm ${showFilters || activeFiltersCount > 0 ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-blue-600'}`} data-id="element-9215">
                  <FilterIcon className="w-5 h-5" data-id="element-9216" />
                  {activeFiltersCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center" data-id="element-9217">
                      {activeFiltersCount}
                    </span>}
                </button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="pt-4 border-t border-slate-200" data-id="element-9218">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-9219">
                  {/* Status Filter */}
                  <div data-id="element-9220">
                    <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-9221">
                      Status
                    </label>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm" data-id="element-9222">
                      <option value="all" data-id="element-9223">Toate</option>
                      <option value="draft" data-id="element-9224">Draft</option>
                      <option value="sent" data-id="element-9225">Trimis</option>
                      <option value="printed" data-id="element-9226">Printat</option>
                      <option value="signed" data-id="element-9227">Semnat</option>
                    </select>
                  </div>

                  {/* Type Filter */}
                  <div data-id="element-9228">
                    <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-9229">
                      Tip Document
                    </label>
                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm" data-id="element-9230">
                      <option value="all" data-id="element-9231">Toate</option>
                      <option value="landlord" data-id="element-9232">Contract Proprietar</option>
                      <option value="tenant" data-id="element-9233">Contract Chiriaș</option>
                    </select>
                  </div>

                  {/* Client Type Filter */}
                  <div data-id="element-9234">
                    <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-9235">
                      Tip Client
                    </label>
                    <select value={clientTypeFilter} onChange={e => setClientTypeFilter(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm" data-id="element-9236">
                      <option value="all" data-id="element-9237">Toate</option>
                      <option value="PF" data-id="element-9238">Persoană Fizică</option>
                      <option value="PJ" data-id="element-9239">Persoană Juridică</option>
                    </select>
                  </div>
                </div>

                {/* Clear Filters Button */}
                {activeFiltersCount > 0 && <div className="mt-4 flex justify-end" data-id="element-9240">
                    <button onClick={clearFilters} className="flex items-center space-x-2 px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors" data-id="element-9241">
                      <XIcon className="w-4 h-4" data-id="element-9242" />
                      <span data-id="element-9243">Șterge filtrele</span>
                    </button>
                  </div>}
              </motion.div>}
          </div>

          <div className="overflow-x-auto" data-id="element-9244">
            <table className="w-full" data-id="element-9245">
              <thead data-id="element-9246">
                <tr className="text-left border-b-2 border-slate-200 bg-slate-50" data-id="element-9247">
                  <th className="py-5 pl-8 font-bold text-slate-700 text-sm uppercase tracking-wide" data-id="element-9248">
                    Tip Document
                  </th>
                  <th className="py-5 px-4 font-bold text-slate-700 text-sm uppercase tracking-wide" data-id="element-9249">
                    Proprietate
                  </th>
                  <th className="py-5 px-4 font-bold text-slate-700 text-sm uppercase tracking-wide" data-id="element-9250">
                    Părți Implicate
                  </th>
                  <th className="py-5 px-4 font-bold text-slate-700 text-sm uppercase tracking-wide" data-id="element-9251">
                    Status
                  </th>
                  <th className="py-5 px-4 font-bold text-slate-700 text-sm uppercase tracking-wide" data-id="element-9252">
                    Valoare
                  </th>
                  <th className="py-5 pr-8 font-bold text-slate-700 text-sm uppercase tracking-wide text-right" data-id="element-9253">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-9254">
                {filteredContracts.length === 0 ? <tr data-id="element-9255">
                    <td colSpan={6} className="py-16 text-center" data-id="element-9256">
                      <FileTextIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" data-id="element-9257" />
                      <h3 className="text-xl font-bold text-slate-700 mb-2" data-id="element-9258">
                        Niciun contract găsit
                      </h3>
                      <p className="text-slate-500 mb-6" data-id="element-9259">
                        {searchQuery || activeFiltersCount > 0 ? 'Încearcă să modifici criteriile de căutare sau filtrele' : 'Creează primul contract pentru a începe'}
                      </p>
                      {!searchQuery && activeFiltersCount === 0 && <button onClick={() => navigate('/inchiriere/contracte/nou')} className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all" data-id="element-9260">
                          <PlusIcon className="w-5 h-5" data-id="element-9261" />
                          <span data-id="element-9262">Creează Contract</span>
                        </button>}
                    </td>
                  </tr> : filteredContracts.map((contract, index) => <motion.tr key={contract.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.05
              }} className="hover:bg-slate-50 transition-colors" data-id="element-9263">
                      <td className="py-6 pl-8" data-id="element-9264">
                        <div className="flex items-center space-x-4" data-id="element-9265">
                          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0" data-id="element-9266">
                            <FileTextIcon className="w-6 h-6" data-id="element-9267" />
                          </div>
                          <div data-id="element-9268">
                            <div className="font-bold text-slate-900 text-base" data-id="element-9269">
                              {contract.type === 'landlord' ? 'Contract Proprietar' : 'Contract Chiriaș'}
                            </div>
                            <div className="text-sm text-slate-500 mt-0.5" data-id="element-9270">
                              {new Date(contract.createdAt).toLocaleDateString('ro-RO')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-6 px-4" data-id="element-9271">
                        <div className="font-medium text-slate-900 max-w-xs" data-id="element-9272">
                          {contract.propertyAddress}
                        </div>
                      </td>
                      <td className="py-6 px-4" data-id="element-9273">
                        <div data-id="element-9274">
                          <div className="text-slate-900 font-semibold" data-id="element-9275">
                            {contract.clientName}
                          </div>
                          <div className="text-slate-500 text-sm mt-0.5" data-id="element-9276">
                            {contract.clientType === 'PF' ? 'Persoană Fizică' : 'Persoană Juridică'}
                          </div>
                        </div>
                      </td>
                      <td className="py-6 px-4" data-id="element-9277">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-bold ${contract.status === 'signed' ? 'bg-emerald-100 text-emerald-700' : contract.status === 'sent' ? 'bg-blue-100 text-blue-700' : contract.status === 'printed' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-9278">
                          {contract.status === 'signed' && <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-9279" />}
                          {contract.status === 'sent' && <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-9280" />}
                          {contract.status === 'signed' ? 'Semnat' : contract.status === 'sent' ? 'Trimis' : contract.status === 'printed' ? 'Printat' : 'Draft'}
                        </span>
                      </td>
                      <td className="py-6 px-4" data-id="element-9281">
                        <div className="font-bold text-slate-900 text-base" data-id="element-9282">
                          {contract.commissionPercent}%
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5" data-id="element-9283">
                          {contract.vatIncluded ? 'TVA inclus' : '+ TVA'}
                        </div>
                      </td>
                      <td className="py-6 pr-8" data-id="element-9284">
                        <div className="flex items-center justify-end space-x-2" data-id="element-9285">
                          <button onClick={e => handleOpenPreview(contract, e)} className="p-2.5 hover:bg-blue-50 rounded-lg text-slate-500 hover:text-blue-600 transition-colors border border-transparent hover:border-blue-200" title="Vezi Contract" data-id="element-9286">
                            <EyeIcon className="w-5 h-5" data-id="element-9287" />
                          </button>
                          <button onClick={() => {
                      setSelectedContract(contract);
                      setIsDetailsOpen(true);
                    }} className="px-4 py-2.5 bg-[#357BEB] text-white rounded-lg text-sm font-semibold hover:bg-[#2868d8] transition-colors shadow-sm" data-id="element-9288">
                            Detalii
                          </button>
                          <button className="p-2.5 hover:bg-green-50 rounded-lg text-slate-500 hover:text-green-600 transition-colors border border-transparent hover:border-green-200" title="Descarcă PDF" data-id="element-9289">
                            <DownloadIcon className="w-5 h-5" data-id="element-9290" />
                          </button>
                          <button className="p-2.5 hover:bg-purple-50 rounded-lg text-slate-500 hover:text-purple-600 transition-colors border border-transparent hover:border-purple-200" title="Imprimă" data-id="element-9291">
                            <PrinterIcon className="w-5 h-5" data-id="element-9292" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Contract Details Drawer */}
      <ContractDetailsDrawer contract={selectedContract} isOpen={isDetailsOpen} onClose={() => {
      setIsDetailsOpen(false);
      setSelectedContract(null);
    }} data-id="element-9293" />

      {/* Contract Preview Modal */}
      {previewContract && <ContractPreviewModal contract={previewContract} isOpen={isPreviewOpen} onClose={() => {
      setIsPreviewOpen(false);
      setPreviewContract(null);
    }} data-id="element-9294" />}
    </div>;
}